import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError } from '@/components/ui/field-error';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  initial: {
    fullName: string;
    displayName: string;
    email: string | null;
  };
}

/**
 * Dialog para editar campos básicos de perfil del tenant desde el admin.
 *
 * NO permite editar:
 *  - WhatsApp (cambiaría isolation y rompería el bot)
 *  - Status (se cambia desde el Select del header)
 *
 * Para esos hay caminos distintos.
 */
export function TenantEditDialog({ open, onOpenChange, tenantId, initial }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setForm({ ...initial, email: initial.email ?? null });
      setIssues({});
    }
  }, [open, initial]);

  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/admin/tenants/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          displayName: form.displayName.trim(),
          email: form.email?.trim() ? form.email.trim() : null,
        }),
      }),
    onSuccess: () => {
      toast.success('Datos del tenant actualizados');
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiCallError && err.status === 400) {
        const body = err.body as { issues?: Record<string, string[]> };
        if (body.issues) setIssues(body.issues);
      } else {
        toast.error('No se pudieron guardar los cambios');
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Editar contacto</DialogTitle>
            <DialogDescription>
              Solo nombre, trato y email. El WhatsApp queda fijo desde el alta.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="fullName" required>
                Nombre completo (fiscal)
              </Label>
              <Input
                id="fullName"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                autoFocus
              />
              <FieldError error={issues.fullName} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="displayName" required>
                Trato (cómo le habla el bot)
              </Label>
              <Input
                id="displayName"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="Juan"
              />
              <FieldError error={issues.displayName} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email ?? ''}
                onChange={(e) => setForm({ ...form, email: e.target.value || null })}
                placeholder="opcional"
              />
              <FieldError error={issues.email} />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
